// src/components/.../EchoLITPost.tsx
"use client";

import React, { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  MessageCircleReply,
  HeartPlus,
  MessageSquareShare,
  SquareArrowOutUpRight,
  AudioWaveform,
} from "lucide-react";

import { selectAuthUser } from "../../../../../../redux/selectors/profile/userSelectors";
import ShareToFriendModalRedux from "../../../../../share/ShareToFriendModalRedux";
import { toDate, formatYMonD } from "../../../../../../utils/helpers/chatDate";
import { logger } from "../../../../../../utils/logging/logger";
import "./EchoLITPost.css";

// Shared share helpers (canonical + text + fallbacks)
import {
  pageCanonicalHref,
  buildShareText,
  shareWithFallback,
} from "../../../../../../utils/share/shareUtils";

const AVAILABILITY_ISO = "2025-09-01";

export default function EchoLITPost() {
  const me = useSelector(selectAuthUser) as { id?: number } | null;

  // Human-friendly availability label
  const availabilityLabel = useMemo(() => {
    const tz =
      (typeof Intl !== "undefined" &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      "UTC";
    return formatYMonD(toDate(AVAILABILITY_ISO), tz);
  }, []);

  // Canonical deep-link to this section (#echolit)
  const buildShareUrl = useCallback((): string => {
    const base = pageCanonicalHref(); // normalize current URL to production domain
    if (!base) return "";
    const u = new URL(base);
    u.hash = "echolit";
    return u.toString();
  }, []);

  // External share (Web Share → Clipboard → Mailto)
  const handleExternalShare = useCallback(async () => {
    try {
      const url = buildShareUrl();
      const shareTitle = "EchoLIT — Your voice. Your story.";
      const shareText = buildShareText(
        shareTitle,
        /* ownerHandle */ undefined,
        url // include URL to encourage clickable previews
      );

      await shareWithFallback({ title: shareTitle, text: shareText, url });
      // Optional: toast on clipboard fallback
      // toast.success("Link copied to clipboard!");
    } catch (e: any) {
      logger.error("External share failed (EchoLIT)", { error: e?.message });
      alert("Share failed. Please try again.");
    }
  }, [buildShareUrl]);

  // Share-to-friend modal state
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <section className="echolit-wrap" id="echolit" aria-label="EchoLIT">
      {/* Header */}
      <header className="echolit-header">
        <div className="echolit-titleblock">
          <div className="echolit-icon" aria-hidden="true">
            <AudioWaveform size={38} />
          </div>
          <div className="echolit-headings">
            <h3 className="echolit-title">EchoLIT</h3>
            <p className="echolit-tagline">Your voice. Your story.</p>
          </div>
        </div>

        {/* Availability Badge */}
        <span
          className="echolit-badge"
          title={`Available by ${availabilityLabel}`}
        >
          Available by {availabilityLabel}
        </span>
      </header>

      {/* Body copy */}
      <div className="echolit-body">
        <p className="echolit-blurb-short">
          Share short audio episodes—from testimonies to teachings—with music or simple storytelling. Replies are quick voice notes.
        </p>
        <p className="echolit-blurb-long">
          EchoLIT makes audio posting feel personal and close. Record, add a light touch of music if you like, and publish.
          Listeners can respond with brief voice comments so conversations stay warm, focused, and easy to follow.
        </p>

        {/* Feature hints */}
        <ul className="echolit-features">
          <li>Create short audio episodes with optional background music</li>
          <li>Voice-only, time-limited replies keep threads concise</li>
          <li>Thoughtful, distraction-free listening and sharing</li>
        </ul>
      </div>

      {/* Actions (unified with other viewers) */}
      <div className="echolit-actions">
        <span
          className="echolit-action echolit-action--muted"
          title="Reactions — Coming Soon. Thanks for your patience!"
          aria-label="Reactions coming soon"
        >
          <HeartPlus size={20} />
        </span>
        <span
          className="echolit-action echolit-action--muted"
          title="Comments — Coming Soon. Stay tuned!"
          aria-label="Comments coming soon"
        >
          <MessageCircleReply size={20} />
        </span>

        {me && (
          <button
            type="button"
            className="echolit-action echolit-action--btn"
            aria-label="Share to a friend"
            title="Share to a friend"
            onClick={() => setShareOpen(true)}
          >
            <MessageSquareShare size={20} />
          </button>
        )}

        <button
          type="button"
          className="echolit-action echolit-action--btn"
          aria-label="Share"
          title="Share"
          onClick={handleExternalShare}
        >
          <SquareArrowOutUpRight size={20} />
        </button>
      </div>

      {/* Share-to-friend modal (uses canonical + #echolit) */}
      <ShareToFriendModalRedux
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={buildShareUrl()}
        title="EchoLIT"
        preview={{
          poster: undefined,
          ownerHandle: undefined,
        }}
      />
    </section>
  );
}
